interface RequestLog {
  url: string;
  timestamp: number;
  source: string;
  stackTrace: string;
}

class NetworkMonitor {
  private logs: RequestLog[] = [];
  private imageRequestCount: Map<string, number> = new Map();
  private enabled: boolean = false;

  enable() {
    this.enabled = true;
    this.interceptImageRequests();
    console.log('[NetworkMonitor] Enabled - tracking all image requests');
  }

  disable() {
    this.enabled = false;
    console.log('[NetworkMonitor] Disabled');
  }

  private interceptImageRequests() {
    if (typeof window === 'undefined') return;

    const originalImageConstructor = window.Image;
    const self = this;

    try {
      window.Image = function(width?: number, height?: number) {
        const img = new originalImageConstructor(width, height);

        const originalSrcDescriptor = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');

        Object.defineProperty(img, 'src', {
          get() {
            return originalSrcDescriptor?.get?.call(this);
          },
          set(value: string) {
            if (self.enabled && value) {
              self.logImageRequest(value, 'Image constructor');
            }
            originalSrcDescriptor?.set?.call(this, value);
          }
        });

        return img;
      } as any;
    } catch (err) {
      console.warn('[NetworkMonitor] Unable to intercept Image constructor:', err);
    }

    // Some browsers/extensions freeze document.createElement; never throw from enable().
    const createElementDesc = Object.getOwnPropertyDescriptor(Document.prototype, 'createElement')
      || Object.getOwnPropertyDescriptor(document, 'createElement');
    if (createElementDesc && createElementDesc.writable === false && !createElementDesc.set) {
      console.warn('[NetworkMonitor] document.createElement is not writable; skipping img intercept');
      return;
    }

    const originalCreateElement = document.createElement.bind(document);
    try {
      document.createElement = function(tagName: string, options?: ElementCreationOptions) {
        const element = originalCreateElement(tagName, options);

        if (tagName.toLowerCase() === 'img') {
          const originalSrcDescriptor = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');

          Object.defineProperty(element, 'src', {
            get() {
              return originalSrcDescriptor?.get?.call(this);
            },
            set(value: string) {
              if (self.enabled && value) {
                self.logImageRequest(value, 'createElement("img")');
              }
              originalSrcDescriptor?.set?.call(this, value);
            }
          });
        }

        return element;
      };
    } catch (err) {
      console.warn('[NetworkMonitor] Unable to intercept document.createElement:', err);
    }
  }

  private logImageRequest(url: string, source: string) {
    const stack = new Error().stack || '';

    const count = (this.imageRequestCount.get(url) || 0) + 1;
    this.imageRequestCount.set(url, count);

    const log: RequestLog = {
      url,
      timestamp: Date.now(),
      source,
      stackTrace: stack
    };

    this.logs.push(log);

    if (url.includes('Organization') || url.includes('Logo')) {
      console.group(`🖼️ [NetworkMonitor] Image Request #${count} - ${url.split('/').pop()}`);
      console.log('Full URL:', url);
      console.log('Source:', source);
      console.log('Request Count:', count);
      console.log('Stack Trace:', stack);
      console.groupEnd();

      if (count > 1) {
        console.warn(`⚠️ DUPLICATE REQUEST for ${url.split('/').pop()} (${count} times)`);
      }
    }
  }

  getLogs(): RequestLog[] {
    return [...this.logs];
  }

  getRequestCounts(): Map<string, number> {
    return new Map(this.imageRequestCount);
  }

  getDuplicateRequests(): Array<{ url: string; count: number }> {
    const duplicates: Array<{ url: string; count: number }> = [];

    this.imageRequestCount.forEach((count, url) => {
      if (count > 1) {
        duplicates.push({ url, count });
      }
    });

    return duplicates.sort((a, b) => b.count - a.count);
  }

  printReport() {
    console.group('📊 Network Monitor Report');
    console.log('Total Requests:', this.logs.length);
    console.log('Unique URLs:', this.imageRequestCount.size);

    const duplicates = this.getDuplicateRequests();
    if (duplicates.length > 0) {
      console.group('⚠️ Duplicate Requests');
      duplicates.forEach(({ url, count }) => {
        console.log(`${count}x - ${url.split('/').pop()}`);
      });
      console.groupEnd();
    } else {
      console.log('✅ No duplicate requests detected');
    }

    console.groupEnd();
  }

  clear() {
    this.logs = [];
    this.imageRequestCount.clear();
    console.log('[NetworkMonitor] Logs cleared');
  }
}

export const networkMonitor = new NetworkMonitor();
